import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { XMarkIcon, MagnifyingGlassIcon, UserIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { subscriptionAPI, mutualisteAPI, offerAPI, formatCurrency } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import toast from 'react-hot-toast';

const CreateSubscriptionModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  
  // États pour la recherche de mutualiste
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMutualiste, setSelectedMutualiste] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // États pour la souscription
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [formData, setFormData] = useState({
    // Champs communs
    startDate: '',
    
    // Champs spécifiques épargne
    monthlyAmount: '',
    
    // Champs spécifiques prêt
    loanAmount: '',
    repaymentMonths: 6 // Valeur par défaut
  });
  const [errors, setErrors] = useState({});

  // Récupérer les offres disponibles
  const { data: offersData, isLoading: offersLoading } = useQuery(
    'offers',
    () => offerAPI.getOffers(),
    {
      enabled: isOpen
    }
  );

  const offers = offersData?.data?.offers || [];

  // Fonction pour obtenir le 1er jour du mois suivant
  const getNextMonthFirstDay = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return nextMonth.toISOString().split('T')[0];
  };

  // Fonction pour obtenir la date d'aujourd'hui au format YYYY-MM-DD
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Initialiser la date de début au 1er jour du mois suivant
  useEffect(() => {
    if (isOpen && !formData.startDate) {
      setFormData(prev => ({
        ...prev,
        startDate: getNextMonthFirstDay()
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fonction pour formater un nombre avec séparateur de milliers
  const formatNumberWithSeparator = (value) => {
    if (!value) return '';
    const numValue = value.toString().replace(/\s/g, '');
    return numValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  // Fonction pour nettoyer le nombre (supprimer les espaces)
  const cleanNumber = (value) => {
    return value.toString().replace(/\s/g, '');
  };

  // Fonction pour calculer le taux d'intérêt (identique au modal mutualiste)
  const getInterestRate = () => {
    if (!selectedOffer || selectedOffer.type !== 'LOAN') return 0;
    if (selectedOffer.features) {
      const interestRateFeature = selectedOffer.features.find(feature =>
        feature.toLowerCase().includes('taux') ||
        feature.toLowerCase().includes('intérêt') ||
        feature.toLowerCase().includes('%')
      );
      if (interestRateFeature) {
        const match = interestRateFeature.match(/(\d+(?:\.\d+)?)\s*%/);
        if (match) {
          return parseFloat(match[1]);
        }
      }
    }
    return 0;
  };

  // Fonction pour calculer le paiement mensuel (identique au modal mutualiste)
  const calculateMonthlyPayment = () => {
    if (!selectedOffer || selectedOffer.type !== 'LOAN' || !formData.loanAmount || !formData.repaymentMonths) {
      return 0;
    }

    const principal = parseFloat(cleanNumber(formData.loanAmount));
    const months = parseInt(formData.repaymentMonths);
    const ratePercent = getInterestRate();
    const rate = ratePercent / 100;

    const totalWithInterest = principal * (1 + rate);
    return Math.ceil(totalWithInterest / months);
  };

  // Validation des champs (identique au modal mutualiste)
  const validateForm = () => {
    const newErrors = {};

    // Date de début obligatoire
    if (!formData.startDate) {
      newErrors.startDate = 'La date de début est obligatoire';
    } else {
      const selectedDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.startDate = 'La date de début ne peut pas être dans le passé';
      }
    }

    // Validation spécifique selon le type d'offre
    if (selectedOffer?.type === 'HEALTH_COVERAGE') {
      // Pas de validation supplémentaire pour FPM (montant fixe)
    } else if (selectedOffer?.type === 'SAVINGS') {
      const minimumAmount = selectedOffer?.price || 10000;
      const monthlyAmountValue = parseFloat(formData.monthlyAmount);
      if (!formData.monthlyAmount || monthlyAmountValue <= 0) {
        newErrors.monthlyAmount = 'Le montant mensuel est obligatoire et doit être positif';
      } else if (monthlyAmountValue < minimumAmount) {
        newErrors.monthlyAmount = `Le montant mensuel minimum est de ${new Intl.NumberFormat('fr-FR').format(minimumAmount)} FCFA`;
      }
    }

    if (selectedOffer?.type === 'LOAN') {
      const loanAmountValue = parseFloat(formData.loanAmount);
      if (!formData.loanAmount || loanAmountValue <= 0) {
        newErrors.loanAmount = 'Le montant du prêt est obligatoire et doit être positif';
      } else if (loanAmountValue < 50000) {
        newErrors.loanAmount = 'Le montant minimum du prêt est de 50 000 FCFA';
      } else if (loanAmountValue > 500000) {
        newErrors.loanAmount = 'Le montant maximum du prêt est de 500 000 FCFA';
      }

      if (!formData.repaymentMonths || formData.repaymentMonths < 1 || formData.repaymentMonths > 10) {
        newErrors.repaymentMonths = 'La durée de remboursement doit être entre 1 et 10 mois';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Gestion des changements d'input (identique au modal mutualiste)
  const handleInputChange = (field, value) => {
    let cleanValue = value;
    if (field === 'monthlyAmount' || field === 'loanAmount') {
      cleanValue = cleanNumber(value);
    }

    // Contrôles de saisie en temps réel - Bloquer dépassement max
    if (field === 'monthlyAmount') {
      const numValue = parseFloat(cleanValue);
      if (cleanValue !== '' && numValue > 1000000) {
        return; // Bloquer épargne > 1 000 000
      }
    }
    
    if (field === 'loanAmount') {
      const numValue = parseFloat(cleanValue);
      if (cleanValue !== '' && numValue > 500000) {
        return; // Bloquer prêt > 500 000
      }
    }
    
    if (field === 'startDate') {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (value !== '' && selectedDate < today) {
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: cleanValue }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Mutation pour créer la souscription
  const createSubscriptionMutation = useMutation(
    (data) => subscriptionAPI.createSubscriptionForUser(data),
    {
      onSuccess: () => {
        toast.success('Souscription créée avec succès');
        queryClient.invalidateQueries('admin-subscriptions');
        queryClient.invalidateQueries('subscription-stats');
        handleClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la création de la souscription');
      }
    }
  );

  // Fonction de recherche de mutualiste
  const searchMutualistes = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await mutualisteAPI.searchMutualistes({
        search: query,
        limit: 10
      });
      setSearchResults(response.data?.data?.mutualistes || []);
    } catch (error) {
      console.error('Erreur recherche mutualistes:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Effet pour la recherche avec délai
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchMutualistes(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleClose = () => {
    setSearchQuery('');
    setSelectedMutualiste(null);
    setSearchResults([]);
    setSelectedOffer(null);
    setFormData({
      startDate: getNextMonthFirstDay(),
      monthlyAmount: '',
      loanAmount: '',
      repaymentMonths: 6
    });
    setErrors({});
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedMutualiste || !selectedOffer) {
      toast.error('Veuillez sélectionner un mutualiste et un produit');
      return;
    }

    if (!validateForm()) {
      return;
    }

    // Préparer les métadonnées selon le type d'offre (identique au modal mutualiste)
    const metadata = {
      offerType: selectedOffer.type,
      startDate: formData.startDate,
      createdByAdmin: true
    };

    if (selectedOffer.type === 'HEALTH_COVERAGE') {
      metadata.fixedAmount = selectedOffer.price;
    } else if (selectedOffer.type === 'SAVINGS') {
      metadata.monthlyAmount = parseFloat(formData.monthlyAmount);
    } else if (selectedOffer.type === 'LOAN') {
      metadata.loanAmount = parseFloat(formData.loanAmount);
      metadata.repaymentMonths = parseInt(formData.repaymentMonths);
      metadata.interestRate = getInterestRate();
      metadata.monthlyPayment = calculateMonthlyPayment();
    }

    const subscriptionData = {
      mutualisteId: selectedMutualiste.id,
      offerId: selectedOffer.id,
      metadata
    };

    createSubscriptionMutation.mutate(subscriptionData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <DocumentTextIcon className="h-8 w-8 text-white" />
                <div>
                  <h3 className="text-lg font-medium text-white">
                    Nouvelle souscription
                  </h3>
                  <p className="text-primary-100 text-sm">
                    Créer une souscription pour un mutualiste
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-primary-100 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Contenu */}
          <form onSubmit={handleSubmit} className="bg-white">
            <div className="px-6 py-6 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Sélection du mutualiste */}
                {!selectedMutualiste ? (
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Rechercher un mutualiste
                    </h4>

                    <div className="relative">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Rechercher par matricule ou nom..."
                        />
                      </div>

                      {/* Résultats de recherche - Dropdown absolu */}
                      {searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                          {searchResults.map((mutualiste) => (
                            <button
                              key={mutualiste.id}
                              type="button"
                              onClick={() => {
                                setSelectedMutualiste(mutualiste);
                                setSearchQuery('');
                                setSearchResults([]);
                              }}
                              className="w-full p-4 hover:bg-blue-50 transition-colors duration-200 text-left border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-semibold text-gray-900">
                                {mutualiste.nom} {mutualiste.prenoms}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                Matricule: {mutualiste.matricule}
                              </div>
                              {mutualiste.user?.email && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {mutualiste.user.email}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Messages d'état */}
                      {isSearching && searchQuery && (
                        <div className="text-center py-4 text-gray-500">
                          Recherche en cours...
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Mutualiste sélectionné */
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <UserIcon className="h-4 w-4 mr-2 text-blue-600" />
                      Mutualiste sélectionné
                    </h3>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-gray-900">
                          {selectedMutualiste.nom} {selectedMutualiste.prenoms}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Matricule: {selectedMutualiste.matricule}
                        </div>
                        {selectedMutualiste.user?.email && (
                          <div className="text-xs text-gray-500 mt-1">
                            {selectedMutualiste.user.email}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedMutualiste(null)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Changer
                      </button>
                    </div>
                  </div>
                )}

                {/* Sélection du produit */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Sélection du produit</h4>
                  </div>

                  {offersLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <LoadingSpinner size="small" />
                      <span className="ml-2 text-sm text-gray-500">Chargement des produits...</span>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {offers.map((offer) => (
                        <label
                          key={offer.id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedOffer?.id === offer.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="offer"
                            value={offer.id}
                            checked={selectedOffer?.id === offer.id}
                            onChange={() => setSelectedOffer(offer)}
                            className="form-radio text-blue-600"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{offer.name}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Options de souscription - Identiques au modal mutualiste */}
              {selectedOffer && (
                <div className="mt-6 bg-green-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Options de souscription</h4>
                  
                  {/* Champs d'options sur une ligne */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Date de début */}
                    <div>
                      <label className="form-label">Date de début *</label>
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                        min={getTodayDate()}
                        className={`form-input ${errors.startDate ? 'border-red-500' : ''}`}
                        required
                      />
                      {errors.startDate && (
                        <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>
                      )}
                    </div>

                    {/* Champs spécifiques selon le type d'offre */}
                    {selectedOffer.type === 'HEALTH_COVERAGE' && (
                      <div>
                        <label className="form-label">Cotisation mensuelle</label>
                        <input
                          type="text"
                          value={formatCurrency(selectedOffer.price)}
                          readOnly
                          className="form-input bg-gray-100 cursor-not-allowed"
                          disabled
                        />
                        <p className="text-gray-500 text-xs mt-1">
                          Montant fixe
                        </p>
                      </div>
                    )}

                    {selectedOffer.type === 'SAVINGS' && (
                      <div>
                        <label className="form-label">Montant mensuel d'épargne *</label>
                        <input
                          type="text"
                          value={formatNumberWithSeparator(formData.monthlyAmount)}
                          onChange={(e) => handleInputChange('monthlyAmount', e.target.value)}
                          onKeyPress={(e) => {
                            if (!/[0-9\s]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          className={`form-input ${errors.monthlyAmount ? 'border-red-500' : ''}`}
                          placeholder="Ex: 25 000"
                          required
                        />
                        {errors.monthlyAmount && (
                          <p className="text-red-500 text-xs mt-1">{errors.monthlyAmount}</p>
                        )}
                        <p className="text-gray-500 text-xs mt-1">
                          Min: {new Intl.NumberFormat('fr-FR').format(selectedOffer.price)} FCFA
                        </p>
                      </div>
                    )}

                    {selectedOffer.type === 'LOAN' && (
                      <>
                        <div>
                          <label className="form-label">Montant du prêt *</label>
                          <input
                            type="text"
                            value={formatNumberWithSeparator(formData.loanAmount)}
                            onChange={(e) => handleInputChange('loanAmount', e.target.value)}
                            onKeyPress={(e) => {
                              if (!/[0-9\s]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            min="50000"
                            max="500000"
                            className={`form-input ${errors.loanAmount ? 'border-red-500' : ''}`}
                            placeholder="Ex: 200 000"
                            required
                          />
                          {errors.loanAmount && (
                            <p className="text-red-500 text-xs mt-1">{errors.loanAmount}</p>
                          )}
                          <p className="text-gray-500 text-xs mt-1">
                            Max: 500 000 FCFA
                          </p>
                        </div>

                        <div>
                          <label className="form-label">Durée (mois) *</label>
                          <select
                            value={formData.repaymentMonths}
                            onChange={(e) => handleInputChange('repaymentMonths', e.target.value)}
                            className={`form-input ${errors.repaymentMonths ? 'border-red-500' : ''}`}
                            required
                          >
                            {[...Array(10)].map((_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {i + 1} mois
                              </option>
                            ))}
                          </select>
                          {errors.repaymentMonths && (
                            <p className="text-red-500 text-xs mt-1">{errors.repaymentMonths}</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Calcul du paiement mensuel pour les prêts */}
                  {selectedOffer.type === 'LOAN' && formData.loanAmount && formData.repaymentMonths && (
                    <div className="bg-blue-50 p-3 rounded-lg mb-4">
                      <h5 className="font-medium text-blue-900 mb-2">Détails du prêt</h5>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-blue-800">
                        <div>
                          <span className="font-medium">Montant:</span><br/>
                          {formatCurrency(parseFloat(cleanNumber(formData.loanAmount)))}
                        </div>
                        <div>
                          <span className="font-medium">Durée:</span><br/>
                          {formData.repaymentMonths} mois
                        </div>
                        <div>
                          <span className="font-medium">Taux:</span><br/>
                          {getInterestRate()}% par an
                        </div>
                        <div>
                          <span className="font-medium">Paiement mensuel:</span><br/>
                          <span className="font-bold">{formatCurrency(calculateMonthlyPayment())}</span>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              )}
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!selectedMutualiste || !selectedOffer || createSubscriptionMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createSubscriptionMutation.isLoading ? (
                    <LoadingSpinner size="small" className="mr-2" />
                  ) : null}
                  Créer la souscription
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateSubscriptionModal;
